import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Collapse from '@material-ui/core/Collapse';
import CircularProgress from '@material-ui/core/CircularProgress';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import InputAdornment from '@material-ui/core/InputAdornment';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import CancelIcon from '@material-ui/icons/Cancel';
import RefreshIcon from '@material-ui/icons/Refresh';
import { green, red } from '@material-ui/core/colors';
import moment from 'moment';
import { instanceAxios } from '../../InstanceAxios';

const useStyles = makeStyles((theme) => ({
  vermelho: {
    color: theme.palette.getContrastText(red[500]),
    backgroundColor: red[500],
  },
  verde: {
    color: '#fff',
    backgroundColor: green[500],
  },
}));

const FORMULARIO_INICIAL = {
  momentoServicoPagamento: 1,
  cnae: '',
  codigoServicoMunicipal: '',
  itemListaServicoLC116: '',
  codigoNbs: '',
  codigoTributacaoNacional: '',
  codigoIndicadorOperacao: '',
  classificacaoTributaria: '',
  situacaoTributaria: '',
  percentualAproximadoTributosSN: '',
  aliquotaIss: '',
  issRetidoFonte: '',
  valor: '0.20',
  descricaoServico: 'Nota teste',
  informacoesAdicionais: '',
  nfseReferenciada: '',
};

const OPCOES_MOMENTO_SERVICO_PAGAMENTO = [
  { valor: 0, label: 'Não informar' },
  { valor: 1, label: 'Fornecimento com pagamento posterior' },
  {
    valor: 2,
    label: 'Recebimento do pagamento com fornecimento já realizado',
  },
  { valor: 3, label: 'Fornecimento com pagamento já realizado' },
  {
    valor: 4,
    label: 'Recebimento do pagamento com fornecimento posterior',
  },
  {
    valor: 5,
    label: 'Fornecimento e recebimento do pagamento concomitantes',
  },
];

const CODIGOS_TRIBUTACAO_COM_MOMENTO_SERVICO_PAGAMENTO = [
  '1005',
  '100501',
  '100502',
  '1509',
  '150901',
  '1712',
  '171201',
  '2505',
  '250501',
];

const removerEspacos = (valor) => String(valor || '').replace(/\s/g, '');

const permiteMomentoServicoPagamento = (codigoTributacaoNacional) =>
  CODIGOS_TRIBUTACAO_COM_MOMENTO_SERVICO_PAGAMENTO.includes(
    removerEspacos(codigoTributacaoNacional)
  );

const normalizarDecimal = (valor) => {
  const valorSemEspacos = removerEspacos(valor);

  return /^\d*(?:[,.]\d{0,4})?$/.test(valorSemEspacos) ? valorSemEspacos : null;
};

const normalizarChaveNfseReferenciada = (valor) =>
  String(valor || '')
    .replace(/\D/g, '')
    .slice(0, 50);

const converterParaNumero = (valor) =>
  Number(String(valor || '').replace(',', '.'));

const obterValor = (objeto, ...campos) => {
  const campo = campos.find(
    (nome) =>
      objeto[nome] !== null && objeto[nome] !== undefined && objeto[nome] !== ''
  );

  return campo ? objeto[campo] : '';
};

const montarFormularioEmpresaENotas = (empresaENotas = {}) => {
  const ibsCbs = empresaENotas.ibsCbs || {};
  const codigoTributacaoNacional = removerEspacos(
    obterValor(ibsCbs, 'codigo_tributacao_nacional', 'codigoTributacaoNacional')
  );

  return {
    ...FORMULARIO_INICIAL,
    cnae: removerEspacos(obterValor(empresaENotas, 'cnae')),
    codigoServicoMunicipal: removerEspacos(
      obterValor(empresaENotas, 'codigoServicoMunicipal')
    ),
    itemListaServicoLC116: removerEspacos(
      obterValor(empresaENotas, 'itemListaServicoLC116')
    ),
    codigoNbs: removerEspacos(obterValor(ibsCbs, 'codigo_nbs', 'codigoNBS')),
    momentoServicoPagamento: permiteMomentoServicoPagamento(
      codigoTributacaoNacional
    )
      ? 1
      : 0,
    codigoTributacaoNacional,
    codigoIndicadorOperacao: removerEspacos(
      obterValor(ibsCbs, 'codigo_indicador_operacao', 'codigoIndicadorOperacao')
    ),
    classificacaoTributaria: removerEspacos(
      obterValor(ibsCbs, 'classificacao_tributaria', 'classificacaoTributaria')
    ),
    situacaoTributaria: removerEspacos(
      obterValor(ibsCbs, 'situacao_tributaria', 'situacaoTributaria')
    ),
    percentualAproximadoTributosSN:
      normalizarDecimal(
        obterValor(
          ibsCbs,
          'percentual_aproximado_tributos_sn',
          'percentualAproximadoTributosSN'
        )
      ) || '',
    aliquotaIss:
      normalizarDecimal(obterValor(empresaENotas, 'aliquotaIss')) || '',
  };
};

function NotaTeste({ idEmpresa, empresaEnotas }) {
  const classes = useStyles();
  const [ready, setReady] = useState(false);
  const [nfe, setNfe] = useState([]);
  const [openAlert, setOpenAlert] = useState(false);
  const [tipoAlert, setTipoAlert] = useState('');
  const [msgAlert, setMsgAlert] = useState('');
  const [openModalNotaTeste, setOpenModalNotaTeste] = useState(false);
  const [tentouEmitirNotaTeste, setTentouEmitirNotaTeste] = useState(false);
  const [emitindoNotaTeste, setEmitindoNotaTeste] = useState(false);
  const [formularioNotaTeste, setFormularioNotaTeste] =
    useState(FORMULARIO_INICIAL);
  const momentoServicoPagamentoHabilitado = permiteMomentoServicoPagamento(
    formularioNotaTeste.codigoTributacaoNacional
  );
  const momentoServicoPagamentoInvalido =
    momentoServicoPagamentoHabilitado &&
    ![1, 2, 3, 4, 5].includes(
      Number(formularioNotaTeste.momentoServicoPagamento)
    );
  const exigeNfseReferenciada =
    Number(formularioNotaTeste.momentoServicoPagamento) === 2;
  const nfseReferenciadaInvalida =
    exigeNfseReferenciada &&
    !/^\d{50}$/.test(formularioNotaTeste.nfseReferenciada);

  const atualizarNfe = async () => {
    setReady(false);
    await instanceAxios(localStorage.getItem('toobiauth'))
      .get(`/nfeteste?idEmpresa=${idEmpresa}`)
      .then((res) => {
        setReady(true);
        setNfe(res.data);
      })
      .catch((err) => {
        setReady(true);
        setMsgAlert('Erro ao listar Nfes');
        setTipoAlert('error');
        setOpenAlert(true);
      });
  };

  const handleClick = (n) => {
    if (n.url_pdf) {
      window.open(n.url_pdf, '_blank');
    } else if (n.nfe_motivo_status) {
      setMsgAlert(n.nfe_motivo_status);
      setTipoAlert('info');
      setOpenAlert(true);
    } else {
      atualizarNfe();
    }
  };

  const abrirModalNotaTeste = () => {
    setOpenModalNotaTeste(true);
    setTentouEmitirNotaTeste(false);
    const dadosEmpresaEnotas = Array.isArray(empresaEnotas)
      ? empresaEnotas[0]
      : empresaEnotas;
    setFormularioNotaTeste(montarFormularioEmpresaENotas(dadosEmpresaEnotas));
  };

  const atualizarFormularioNotaTeste =
    (campo, normalizar = (valor) => valor) =>
    (event) => {
      const valor = normalizar(event.target.value);

      setFormularioNotaTeste((formularioAnterior) => {
        if (valor === null) {
          return formularioAnterior;
        }

        const formularioAtualizado = {
          ...formularioAnterior,
          [campo]: valor,
        };

        if (campo === 'codigoTributacaoNacional') {
          formularioAtualizado.momentoServicoPagamento =
            permiteMomentoServicoPagamento(valor) ? 1 : 0;
          formularioAtualizado.nfseReferenciada = '';
        }

        if (campo === 'momentoServicoPagamento' && Number(valor) !== 2) {
          formularioAtualizado.nfseReferenciada = '';
        }

        return formularioAtualizado;
      });
    };

  const atualizarCampoSemEspacos = (campo) =>
    atualizarFormularioNotaTeste(campo, removerEspacos);

  const emitirNotaTeste = async () => {
    setTentouEmitirNotaTeste(true);

    if (momentoServicoPagamentoInvalido) {
      setMsgAlert('Informe o momento do serviço e do pagamento');
      setTipoAlert('warning');
      setOpenAlert(true);
      return;
    }

    if (nfseReferenciadaInvalida) {
      setMsgAlert('Informe os 50 dígitos da NFS-e referenciada');
      setTipoAlert('warning');
      setOpenAlert(true);
      return;
    }

    const {
      codigoNbs,
      informacoesAdicionais,
      momentoServicoPagamento,
      nfseReferenciada,
      ...camposFormulario
    } = formularioNotaTeste;
    const payloadNotaTeste = {
      ...camposFormulario,
      id_empresa: idEmpresa,
      codigoNBS: codigoNbs,
      observacoes: informacoesAdicionais,
      tipoOperacao: momentoServicoPagamento,
      issRetidoFonte:
        formularioNotaTeste.issRetidoFonte === ''
          ? false
          : formularioNotaTeste.issRetidoFonte,
      percentualAproximadoTributosSN: converterParaNumero(
        formularioNotaTeste.percentualAproximadoTributosSN
      ),
      aliquotaIss: converterParaNumero(formularioNotaTeste.aliquotaIss),
      valor: converterParaNumero(formularioNotaTeste.valor),
      ...(exigeNfseReferenciada ? { nfseReferenciada } : {}),
    };

    setEmitindoNotaTeste(true);
    await instanceAxios(localStorage.getItem('toobiauth'))
      .post('/nfeteste', payloadNotaTeste)
      .then(() => {
        setOpenModalNotaTeste(false);
        setMsgAlert('Nota fiscal emitida com sucesso. Aguarde a autorização.');
        setTipoAlert('success');
        setOpenAlert(true);
        atualizarNfe();
      })
      .catch((err) => {
        const mensagemErro =
          err.response?.data?.message ||
          'Erro ao emitir a nota fiscal de teste';
        window.alert(mensagemErro);
      })
      .finally(() => {
        setEmitindoNotaTeste(false);
      });
  };

  const cancelNfe = async (nfe) => {
    console.log(nfe);
    setReady(false);
    await instanceAxios(localStorage.getItem('toobiauth'))
      .delete(`/nfeteste/${nfe.id}`)
      .then((res) => {
        setMsgAlert(
          'Cancelamento da nota solicitado,espere a autorização da mesma!'
        );
        setTipoAlert('success');
        setOpenAlert(true);
        atualizarNfe();
      })
      .catch((err) => {
        setMsgAlert(
          'Erro ao solicitar cancelamento nota fiscal,tente novamente!'
        );
        setTipoAlert('error');
        setOpenAlert(true);
        setReady(true);
      });
  };

  useEffect(() => {
    atualizarNfe();
  }, []);

  return (
    <>
      <Collapse in={!ready}>
        <CircularProgress style={{ marginLeft: '50%' }} />
      </Collapse>
      <Collapse in={ready}>
        <Grid container spacing={3} direction="row" justify="center">
          <h1>Notas Teste</h1>
        </Grid>
        <Grid
          container
          direction="row"
          justify="space-between"
          alignItems="space-between"
        >
          <Grid item>
            <List>
              {nfe.map((n) => (
                <ListItem button onClick={() => handleClick(n)}>
                  <ListItemAvatar>
                    <Avatar
                      className={
                        n.nfe_status === 'Autorizada'
                          ? classes.verde
                          : n.nfe_status === 'Negada' ||
                            n.nfe_status === 'Cancelada'
                          ? classes.vermelho
                          : ''
                      }
                    >
                      <InsertDriveFileIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={n.nfe_status}
                    secondary={moment(n.data_emissao).format('L')}
                  />
                  {n.nfe_status === 'Autorizada' ? (
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => cancelNfe(n)}
                        color="secondary"
                      >
                        <CancelIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  ) : null}
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item>
            <IconButton
              color="primary"
              aria-label="add an alarm"
              onClick={atualizarNfe}
            >
              <RefreshIcon />
            </IconButton>
          </Grid>
        </Grid>
        <Grid
          container
          spacing={3}
          direction="row"
          justify="center"
          alignItems="flex-end"
        >
          <Button
            variant="contained"
            color="primary"
            onClick={abrirModalNotaTeste}
          >
            Nota teste
          </Button>
        </Grid>
      </Collapse>
      <Dialog
        open={openModalNotaTeste}
        onClose={() => setOpenModalNotaTeste(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Configurar nota teste</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Momento do serviço e do pagamento"
                required={momentoServicoPagamentoHabilitado}
                disabled={!momentoServicoPagamentoHabilitado}
                error={tentouEmitirNotaTeste && momentoServicoPagamentoInvalido}
                helperText={
                  tentouEmitirNotaTeste && momentoServicoPagamentoInvalido
                    ? 'Selecione uma opção'
                    : ''
                }
                value={
                  momentoServicoPagamentoHabilitado
                    ? formularioNotaTeste.momentoServicoPagamento
                    : 0
                }
                onChange={atualizarFormularioNotaTeste(
                  'momentoServicoPagamento'
                )}
              >
                {OPCOES_MOMENTO_SERVICO_PAGAMENTO.map((opcao) => (
                  <MenuItem key={opcao.valor} value={opcao.valor}>
                    {opcao.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {exigeNfseReferenciada ? (
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="NFS-e referenciada"
                  value={formularioNotaTeste.nfseReferenciada}
                  error={tentouEmitirNotaTeste && nfseReferenciadaInvalida}
                  helperText={
                    tentouEmitirNotaTeste && nfseReferenciadaInvalida
                      ? 'Informe exatamente 50 dígitos'
                      : ''
                  }
                  onChange={atualizarFormularioNotaTeste(
                    'nfseReferenciada',
                    normalizarChaveNfseReferenciada
                  )}
                  inputProps={{
                    inputMode: 'numeric',
                    maxLength: 50,
                    pattern: '[0-9]*',
                  }}
                />
              </Grid>
            ) : null}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="CNAE"
                value={formularioNotaTeste.cnae}
                onChange={atualizarCampoSemEspacos('cnae')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Código serviço municipal"
                value={formularioNotaTeste.codigoServicoMunicipal}
                onChange={atualizarCampoSemEspacos('codigoServicoMunicipal')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Item lista de serviço - LC 116"
                value={formularioNotaTeste.itemListaServicoLC116}
                onChange={atualizarCampoSemEspacos('itemListaServicoLC116')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Código NBS"
                value={formularioNotaTeste.codigoNbs}
                onChange={atualizarCampoSemEspacos('codigoNbs')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Código de tributação nacional"
                value={formularioNotaTeste.codigoTributacaoNacional}
                onChange={atualizarCampoSemEspacos('codigoTributacaoNacional')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Indicador da operação"
                value={formularioNotaTeste.codigoIndicadorOperacao}
                onChange={atualizarCampoSemEspacos('codigoIndicadorOperacao')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Classificação tributária"
                value={formularioNotaTeste.classificacaoTributaria}
                onChange={atualizarCampoSemEspacos('classificacaoTributaria')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Situação tributária (CST)"
                value={formularioNotaTeste.situacaoTributaria}
                onChange={atualizarCampoSemEspacos('situacaoTributaria')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Percentual aproximado de tributos"
                value={formularioNotaTeste.percentualAproximadoTributosSN}
                onChange={atualizarFormularioNotaTeste(
                  'percentualAproximadoTributosSN',
                  normalizarDecimal
                )}
                inputProps={{ inputMode: 'decimal' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Alíquota ISS"
                value={formularioNotaTeste.aliquotaIss}
                onChange={atualizarFormularioNotaTeste(
                  'aliquotaIss',
                  normalizarDecimal
                )}
                inputProps={{ inputMode: 'decimal' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="ISS retido na fonte"
                value={formularioNotaTeste.issRetidoFonte}
                onChange={atualizarFormularioNotaTeste('issRetidoFonte')}
              >
                <MenuItem value={true}>Sim</MenuItem>
                <MenuItem value={false}>Não</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                disabled
                fullWidth
                label="Valor"
                value={formularioNotaTeste.valor}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">R$</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                multiline
                fullWidth
                rows={3}
                label="Descrição do serviço"
                value={formularioNotaTeste.descricaoServico}
                onChange={atualizarFormularioNotaTeste('descricaoServico')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                multiline
                fullWidth
                rows={3}
                label="Informações adicionais"
                value={formularioNotaTeste.informacoesAdicionais}
                onChange={atualizarFormularioNotaTeste('informacoesAdicionais')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModalNotaTeste(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={emitirNotaTeste}
            disabled={emitindoNotaTeste}
          >
            Emitir nota teste
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={openAlert}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        style={{ zIndex: 2000 }}
        onClose={() => setOpenAlert(!openAlert)}
      >
        <Alert severity={tipoAlert} onClick={() => setOpenAlert(!openAlert)}>
          {msgAlert}
        </Alert>
      </Snackbar>
    </>
  );
}

export default NotaTeste;
